import React, { useState, useEffect, useRef } from 'react'
import {
  withStyles,
  MenuItem,
  FormControl,
  Select,
  Divider,
  Typography,
  FormControlLabel,
} from '@material-ui/core'
import styles from './styles'
import { useSelector, useDispatch } from 'react-redux'
import CustomizedSwitch from 'src/components/customized-switch'
import { CoreButton, CoreTextField } from 'src/components/core'
import { Icon } from '@iconify/react'
import translator from 'src/translate'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { domainListAction } from 'src/reducers/domain/action'
import { useHistory } from 'react-router-dom'
import { track } from '@amplitude/analytics-browser'
import { HtmlToPdf } from '@minicoursegenerator/html-to-pdf-converter'
import UserType from 'src/model/user-types'

const DEFAULT_SHARE_DOMAIN = process.env.REACT_APP_VIEW_URL?.replace(/^https?:\/\//, '').replace(/\/$/, '') || 'share.minicoursegenerator.com'

const ShareLinkCourse = props => {
  const { classes, handleView, me, onMoveToDraft, canMoveToDraft } = props
  const dispatch = useDispatch()
  const course = useSelector(state => state.course.course)
  const entity = useSelector(state => state.course.entity)
  const landingPageSettings = useSelector(state => state.course.landingPageSettings)
  const pdfDownloadRef = useRef(null)
  const userControl = UserType[me?.userRoleId]
  const isBasicUser =
    userControl == 'Basic' ||
    userControl == 'AppSumo (Tier 1)' ||
    UserType.isTrialEquivalent(userControl)
  const [copiedShareCode, setCopiedShareCode] = useState(false)
  const [showShareButtons, setShowShareButtons] = useState(0)
  const [domains, setDomains] = useState()
  const history = useHistory()
  useEffect(() => {
    const query = {
      CurrentPage: 0,
      // Every domain of the workspace, not just the latest five: the picker lists them all.
      PageSize: 200,
      SortingColumn: 'LastModifiedAt',
      Asc: false,
      FilterText: '',
      Filters: [],
    }

    dispatch(
      domainListAction({ ...query, SortingColumn: 'LastModifiedAt' }, data => {
        setDomains(data.results)
        const firstVerified = data.results.find(x => x.verificationStatus == 1)
        setDomainName(firstVerified?.domain || DEFAULT_SHARE_DOMAIN)
      }),
    )
  }, [])

  const onClickShareCode = () => {
    setCopiedShareCode(true)
    setTimeout(() => {
      setCopiedShareCode(false)
    }, 1000)
  }

  const customDomain = useSelector(state => state.domain.list)

  const customDomainList = customDomain && customDomain.results
  const collectionDomain =
    customDomainList &&
    customDomainList.filter(
      item =>
        item.defaultCollectionDto != null &&
        item.defaultCollectionDto.id == course.id &&
        item.verificationStatus == 1,
    )
  const domain =
    collectionDomain?.length > 0
      ? collectionDomain[0].domain
      : DEFAULT_SHARE_DOMAIN

  const [domainName, setDomainName] = useState(domain)

  const handleDomain = e => {
    setDomainName(e.target.value)
  }
  let shareCode = showShareButtons ? '?shr=1' : ''

  const data = [
    {
      title: 'Embed on a Website',
      icon: 'iconoir:code',
      color: '#FB9D01',
      onClick: () => handleView('embed-course'),
    },
    {
      title: 'iFrame Code',
      icon: 'ph:devices',
      color: '#29D25D',
      onClick: () => handleView('iframe-course'),
    },
    {
      title: 'Download as PDF',
      icon: 'hugeicons:pdf-01',
      color: '#ED4245',
      onClick: () => {
        track('download_as_pdf_clicked')
        if (isBasicUser) {
          handleView('pdf-course')
        } else if (pdfDownloadRef.current) {
          track('course_downloaded_as_pdf')
          pdfDownloadRef.current()
        }
      },
    },
    {
      title: 'Download as SCORM',
      icon: 'ph:cube',
      color: '#5DADFF',
      onClick: () => {
        track('download_as_scorm_clicked')
        handleView('scorm-course')
      },
    },
  ]

  return (
    <div className={classes.shareViewRoot}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <Typography fontSize={'14px'} fontWeight={400} fontFamily={'Inter'}>
          Choose your domain
        </Typography>
        <FormControl
          variant="outlined"
          className={classes.shareViewFormControl}
        >
          <Select
            labelId="domain-selection-label"
            id="domain-selection-select"
            fullWidth
            value={domainName}
            disabled={false}
            onChange={handleDomain}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    marginLeft: '8px',
                    marginRight: '8px',
                    borderRadius: '8px',
                    marginTop: '4px',
                    marginBottom: '4px',
                  },
                },
              },
            }}
          >
            <MenuItem value={DEFAULT_SHARE_DOMAIN}>
              (Default) {DEFAULT_SHARE_DOMAIN}
            </MenuItem>

            {domains &&
              domains
                .filter(x => x.verificationStatus == 1)
                .map(value => {
                  return (
                    <MenuItem value={value.domain} key={value.domain}>
                      {value.domain}
                    </MenuItem>
                  )
                })}
          </Select>
        </FormControl>

        <Typography
          fontSize="12px"
          fontWeight={400}
          color="#015FFB"
          onClick={() => {
            history.push('/workspace/custom-domain')
          }}
          style={{
            cursor: 'pointer',
          }}
        >
          Want to customize the domain of the links?
        </Typography>
      </div>

      <CoreTextField
        fullWidth
        value={'https://' + domainName + `/${course.code}` + shareCode}
        label="Course link"
      />

      {/* Copy Link and Move to draft share a row: the link is the reason the panel is open, so it
          keeps the emphasis, and moving to draft sits beside it rather than tucked underneath. */}
      <div className={classes.shareViewLinkActions}>
        <CopyToClipboard
          text={'https://' + domainName + `/${course.code}` + shareCode}
        >
          <CoreButton
            className={classes.shareViewLinkAction}
            onClick={onClickShareCode}
            variant="contained"
            color="primary"
          >
            {copiedShareCode ? 'Copied!' : 'Copy Link'}
          </CoreButton>
        </CopyToClipboard>

        {canMoveToDraft && (
          <CoreButton
            className={classes.shareViewLinkAction}
            color="secondary"
            variant="text"
            onClick={onMoveToDraft}
            startIcon={<Icon icon="mdi:eye-off-outline" />}
          >
            Move to draft
          </CoreButton>
        )}
      </div>

      <Divider className={classes.shareViewDivider} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        {data.map((item, index) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              justifyContent: 'center',
              width: '80px',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={item.onClick}
          >
            <div className={classes.iconDiv}>
              <Icon icon={item.icon} color={item.color} fontSize={24} />
            </div>
            <Typography
              fontSize={'14px'}
              fontWeight={400}
              fontFamily={'Inter'}
              textAlign={'center'}
            >
              {item.title}
            </Typography>
          </div>
        ))}
      </div>
      {entity && !isBasicUser && (() => {
        const headerSection = landingPageSettings?.sections?.find(
          x => x.type === 'Header',
        )
        const mainSection = landingPageSettings?.sections?.find(
          x => x.type === 'Main',
        )
        const headerImage = headerSection?.content?.image
        const headerImageUrl = headerImage
          ? headerImage.includes('http')
            ? headerImage
            : `https://cdn.minicoursegenerator.com/${headerImage}`
          : ''
        const headerImageHtml = headerImageUrl
          ? `<div style="text-align:center;margin-bottom:16px;"><img src="${headerImageUrl}" style="max-width:100%;height:auto;" /></div>`
          : ''
        const mainHtml = mainSection?.content?.html || ''
        const alphabet = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','R','S','T','U','V','Y','Z']
        // Result, badge, score, signup, payment, protection kartlarını atla
        const skipTypes = [10, 11, 12, 13, 14, 15, 16, 18]
        const cardToHtml = (card) => {
          if (!card || skipTypes.includes(card.type)) return ''
          if (!card.text) return ''
          // Şıkları oluştur
          let selectionsHtml = ''
          if (card.selectionDtos && card.selectionDtos.length > 0) {
            selectionsHtml += '<div style="margin:8px 0 16px 0;">'
            card.selectionDtos.forEach((option, i) => {
              const isCorrect = option.isCorrect
              const bg = isCorrect ? 'background-color:#e8f5e9;border-color:#66bb6a;' : ''
              selectionsHtml += `<div style="padding:8px 12px;margin:4px 0;border:1px solid #e0e0e0;border-radius:6px;${bg}"><strong>${alphabet[i] || (i+1)}</strong> - ${option.text || ''}</div>`
            })
            selectionsHtml += '</div>'
          }
          if (!selectionsHtml) return card.text
          // Soru metnini p içine sar ve şıkları column'a inject et
          const tmp = document.createElement('div')
          tmp.innerHTML = card.text
          const column = tmp.querySelector('.column')
          if (column) {
            // Eğer column'un direkt text node'ları varsa (p içinde değilse), p ile sar
            const childNodes = Array.from(column.childNodes)
            childNodes.forEach(node => {
              if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                const p = document.createElement('p')
                p.textContent = node.textContent
                column.replaceChild(p, node)
              }
            })
            column.insertAdjacentHTML('beforeend', selectionsHtml)
            return tmp.innerHTML
          }
          return card.text + selectionsHtml
        }
        const cardHtml = entity?.cardDtos?.map(card => cardToHtml(card)).join('') || ''
        const finalHtml = headerImageHtml + mainHtml + cardHtml
        console.log('PDF HTML:', finalHtml)
        return (
          <HtmlToPdf
            html={finalHtml}
            fontFamily="Lato"
            fontUrl="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
            fileName={(entity?.name || 'document') + '.pdf'}
          >
            {({ download }) => {
              pdfDownloadRef.current = download
              return null
            }}
          </HtmlToPdf>
        )
      })()}
    </div>
  )
}

export default withStyles(styles)(ShareLinkCourse)
